<script>
	import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
	import { voiceState } from "@app/store/voiceStore.svelte.js";
	import { toast } from '@zerodevx/svelte-toast';
	import { fade } from "svelte/transition";
	import { onMount } from "svelte";
	import { Circle } from "svelte-loading-spinners";
	import voiceLib from "@cclib/providers/textToSpeechAll/voices.js";
	import { environment } from "@app/store/envStore.svelte.js";
	import {
		WEB_SPEECH_PROVIDER,
		cancel,
		getWebSpeechVoiceCatalog,
		isLowQualityVoice,
		isNetworkDependentVoice,
		isWebSpeechSupported,
		speak,
	} from "@app/lib/audio/webSpeechTts.js";
	import {
		LOCAL_TTS_PROVIDER,
		getParentVoices,
		renderParentAudio,
	} from "@app/lib/audio/parentTts.js";
	/* Firebase */
	import firebase from "@app/configs/firebase.js";
	import "firebase/compat/functions";
    import * as m from "@app/paraglide/messages.js";

	const OFFLINE_VOICE_LANGUAGE = "Offline";

	let userId = $state();
	let serviceProvider = $state("all"),
		gender = $state("all"),
		language = $state(environment.online ? "English - US" : OFFLINE_VOICE_LANGUAGE),
		rendering = $state(false),
		audioPlayer,
		selectedVoice = $state(),
		previewText = $state(m.voice_manager_default_preview_text()),
		offlineVoiceCatalog = $state({ male: [], female: [] }),
		parentDefaultVoiceId = $state(null);

	let webSpeechAvailable = $derived(isWebSpeechSupported());
	// When the AD license is active, the parent host (e.g. Unity) supplies the
	// "Offline" voice catalog through PenPal instead of the browser web-speech
	// voices.
	let useParentVoices = $derived(environment.adLicense);
	let languageOptions = $derived.by(() => {
		let options = Object.keys(voiceLib);
		return (webSpeechAvailable || useParentVoices) ? [...options, OFFLINE_VOICE_LANGUAGE] : options;
	});
	let voiceCatalog = $derived(language === OFFLINE_VOICE_LANGUAGE ? offlineVoiceCatalog : voiceLib[language]);
	let hasLowQualityOfflineVoices = $derived.by(() => {
		return [...offlineVoiceCatalog.male, ...offlineVoiceCatalog.female].some((voice) => {
			return isLowQualityVoice(voice);
		});
	});

	onMount(() => {
		try {
			audioPlayer = document.getElementById("adPreviewPlayer");
		} catch (err) {
			console.log(err, err.message);
		}

		if (useParentVoices) {
			loadParentVoices();
		} else {
			loadOfflineVoices();
		}

		if (environment.online) {
			loadElevenLabsVoices();
		}

		if (!useParentVoices && webSpeechAvailable && window.speechSynthesis) {
			window.speechSynthesis.addEventListener("voiceschanged", loadOfflineVoices);

			return () => {
				cancel();
				window.speechSynthesis.removeEventListener("voiceschanged", loadOfflineVoices);
			};
		}
	});

	function normalizeOfflineVoice(voice, fallbackGender) {
		return {
			...voice,
			provider: WEB_SPEECH_PROVIDER,
			id: voice.id || voice.voiceURI || voice.name,
			voiceURI: voice.voiceURI || voice.id || voice.name,
			name: voice.name,
			styles: voice.styles || ["default"],
			gender: voice.gender || fallbackGender,
			language: voice.lang || voice.language || OFFLINE_VOICE_LANGUAGE,
		};
	}

	function normalizeParentGender(gender) {
		const value = typeof gender === "string" ? gender.trim().toLowerCase() : "";
		return value === "female" ? "female" : "male";
	}

	function normalizeParentVoice(voice) {
		return {
			provider: LOCAL_TTS_PROVIDER,
			id: voice.id,
			name: voice.name || voice.id,
			styles: ["default"],
			gender: normalizeParentGender(voice.gender),
			language: OFFLINE_VOICE_LANGUAGE,
			sourceLanguage: voice.language || null,
		};
	}

	async function loadOfflineVoices() {
		if (!webSpeechAvailable) return;

		const catalog = await getWebSpeechVoiceCatalog({ groupByGender: true });
		offlineVoiceCatalog = {
			male: (catalog.male || []).map((voice) => normalizeOfflineVoice(voice, "male")),
			female: (catalog.female || []).map((voice) => normalizeOfflineVoice(voice, "female")),
		};
	}

	async function loadParentVoices() {
		try {
			const res = await getParentVoices();
			if (!res || res.success === false) {
				throw new Error(res?.message || m.voice_manager_no_data_error());
			}

			const voices = Array.isArray(res.voices) ? res.voices : [];
			parentDefaultVoiceId = res.defaultVoice || null;
			const normalized = voices.map((voice) => normalizeParentVoice(voice));
			offlineVoiceCatalog = {
				male: normalized.filter((voice) => voice.gender === "male"),
				female: normalized.filter((voice) => voice.gender === "female"),
			};

			if (parentDefaultVoiceId) {
				selectedVoice =
					offlineVoiceCatalog.male.find((voice) => voice.id === parentDefaultVoiceId) ||
					offlineVoiceCatalog.female.find((voice) => voice.id === parentDefaultVoiceId) ||
					selectedVoice;
			}
		} catch (err) {
			console.log(err, err.message);
			toast.push(m.voice_manager_render_failed_toast({ error: err.message }), {
				classes: ["toast-danger"],
			});
		}
	}

	function saveVoicesToLocalStorage() {
		localStorage.setItem("cc-voice-defaults", JSON.stringify(voiceState));
	}

	function pinVoice(v) {
		voiceState.push(v);
		toast.push(m.voice_manager_pin_success_toast({ name: v.name }), {
            classes: ["toast-success"],
        });

		saveVoicesToLocalStorage();
	}

	function unPinVoice(v) {
        //remove the voice from the voiceState array without assigning to the voiceState import.

        let index = voiceState.findIndex((voice) => voice.name === v.name);
        if (index !== -1) {
            voiceState.splice(index, 1);
        }
        
		/* voiceState = voiceState.filter((voice) => {
			return voice.name !== v.name;
		}); */

		toast.push(m.voice_manager_unpin_success_toast({ name: v.name }), {
            classes: ["toast-success"],
        });

		saveVoicesToLocalStorage();
	}

	async function previewVoice(v) {
		rendering = true;
		selectedVoice = v;
		try {
			rendering = true;
			if (v.provider === WEB_SPEECH_PROVIDER) {
				cancel();
				if (audioPlayer) {
					audioPlayer.pause();
					audioPlayer.removeAttribute("src");
				}
				await speak(previewText || m.voice_manager_default_preview_text(), {
					voiceURI: v.voiceURI,
					rate: 1,
					pitch: 1,
					volume: 1,
				});
			} else if (v.provider === LOCAL_TTS_PROVIDER) {
				const result = await renderParentAudio({
					text: previewText || m.voice_manager_default_preview_text(),
					voice: v.id,
					speed: 1,
					format: "mp3",
				});

				if (!result || result.success === false || !result.url) {
					throw new Error(result?.message || m.voice_manager_no_data_error());
				}

				audioPlayer.src = result.url;
				await audioPlayer.load();
				await audioPlayer.play();
			} else if (selectedVoice.preview_url) {
				audioPlayer.src = selectedVoice.preview_url;
				await audioPlayer.load();
				await audioPlayer.play();
			} else {
				if (!environment.online) {
					throw new Error("Offline voices are not available in this browser.");
				}
				let res = await firebase.functions().httpsCallable("v8TextToSpeechProviderV4")({
					provider: v.provider,
					language: v.language,
					voice: v.name,
                    voice_id : v.id,
                    stability : 0.5,
                    similarity : 0.5,
					speed: 1,
					pitch: 0,
					style: "default",
					audioEncoding: "MP3",
					preview: true,
					text: previewText || m.voice_manager_default_preview_text(),
				});
				console.log(res);
				if (res.data) {
					audioPlayer.src = res.data.audio;
					await audioPlayer.load();
					await audioPlayer.play();
				} else {
					throw new Error(m.voice_manager_no_data_error());
				}
			}
		} catch (err) {
			console.log(err, err.message);
			toast.push(m.voice_manager_render_failed_toast({ error: err.message }), {
                classes: ["toast-danger"],
            });
		} finally {
			rendering = false;
		}
	}

	async function loadElevenLabsVoices() {
		if (!environment.online) return;

        try {
			const currentUser = firebase.auth().currentUser;
			if (!currentUser) return;

			userId = currentUser.uid;
            let elevenLabsVoices = await firebase.functions().httpsCallable("v8ElevenLabsGetVoices")(userId);
            elevenLabsVoices.data.forEach((elevenLabsVoice) => {
                let gender = elevenLabsVoice.labels && elevenLabsVoice.labels.gender === "female" ? "female" : "male";
                voiceLib.ElevenLabs[gender] = [
                    ...voiceLib.ElevenLabs[gender],
                    {
                        "provider": "ElevenLabs",
                        "id": elevenLabsVoice.voice_id,
                        "name": elevenLabsVoice.name,
                        "styles": ["default", "eleven_multilingual_v2"],
                        "preview_url": elevenLabsVoice.preview_url,
                        "gender": gender,
                        "language": "ElevenLabs",
                        "high_quality_base_model_ids": elevenLabsVoice.high_quality_base_model_ids,
                    },
                ];
            });
        } catch(err){
            console.log(err, err.message);
        }		
	}
</script>

<div 
    transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'voiceManager' ? 'show d-block' : ''}" 
    role="dialog" 
    tabindex="-1" 
    id="VoiceManagerModal"
    aria-labelledby="voiceManagerTitle"
>
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
				<h4 class="modal-title" id="voiceManagerTitle">{m.voice_manager_title()}</h4>
				<button type="button" class="btn-close" id="voiceManagerClose" aria-label={m.close()} onclick={hideModal}></button>
                <audio id="adPreviewPlayer" class="d-none" autobuffer="autobuffer" preload="metadata">
                    <source src="" type="audio/wav" />
                </audio>
            </div>
            <div class="modal-body">
                <div class="row">
                    <div class="col-2">
						<p class="lead" id="filterSection">{m.voice_manager_filter_label()}</p>
                        <form aria-labelledby="filterSection">
                            <div class="mb-3">
								<label class="form-label" for="LanguageSelectDropdown">{m.voice_manager_language_label()}</label>
                                <select class="form-select" id="LanguageSelectDropdown" bind:value={language}>
                                    {#each languageOptions as lang (lang)}
                                        <option>{lang}</option>
                                    {/each}
                                </select>
                            </div>
                            <div class="mb-3">
							<label class="form-label" for="genderDropdown">{m.voice_manager_gender_label()}</label>
								<select class="form-select" id="genderDropdown" bind:value={gender}>
								<option value="all">{m.all()}</option>
								<option value="male">{m.voice_manager_gender_male()}</option>
								<option value="female">{m.voice_manager_gender_female()}</option>
								</select>
							</div>
							<div class="mb-3">
							<label class="form-label" for="serviceProviderDropdown">{m.voice_manager_provider_label()}</label>
								<select class="form-select" id="serviceProviderDropdown" bind:value={serviceProvider}>
								<option value="all">{m.all()}</option>
								<option value="amazon">{m.voice_manager_provider_amazon()}</option>
								<option value="microsoft">{m.voice_manager_provider_microsoft()}</option>
								<option value="google">{m.voice_manager_provider_google()}</option>
									<option value="deepgram">{m.voice_manager_provider_deepgram()}</option>
								<option value="ElevenLabs">{m.voice_manager_provider_eleven_labs()}</option>
								{#if webSpeechAvailable}
									<option value={WEB_SPEECH_PROVIDER}>{m.voice_manager_provider_offline()}</option>
								{/if}
								</select>
							</div>
							<hr />
							<div class="mb-3">
							<label class="form-label" for="previewTextInput">{m.voice_manager_preview_text_label()} <span class="small text-muted">({previewText.length}/100)</span></label>
								<textarea bind:value={previewText} class="form-control text-muted" rows="5" maxlength="100"></textarea>
							</div>
                        </form>
                    </div>
                    <div class="col-7">
						<p class="lead" id="allVoicesSection">{m.voice_manager_all_voices_label()}</p>
						{#if language === OFFLINE_VOICE_LANGUAGE && hasLowQualityOfflineVoices}
							<div class="alert alert-warning small" role="alert">
								{m.voice_manager_offline_low_quality_warning()}
							</div>
						{/if}
                        <div class="container rounded pt-3 scrollArea" role="region" aria-labelledby="allVoicesSection">
                            <div class="row g-3">
                                {#if gender === "all" || gender === "male"}
                                    {#each (voiceCatalog?.male || []).filter((v) => {
                                        return serviceProvider === "all" || serviceProvider === v.provider;
                                    }) as voice (voice.id || voice.name)}
                                        <div class="col-4">
                                            <div class="card border-secondary">
                                                <div class="card-body">
                                                    <h5 class="card-title">
                                                        <i class="bi bi-person-circle" aria-hidden="true"></i> {voice.name}
                                                    </h5>
													<h6 class="card-subtitle mb-2 text-muted small">{m.voice_manager_gender_male()}</h6>
                                                    {#if voiceState.findIndex((v) => v.name === voice.name) > -1}
                                                        <button type="button" 
                                                            id="unpin_{voice.name}" 
                                                            class="btn btn-primary btn-sm" 
															aria-label={m.voice_manager_unpin_label({ name: voice.name })} 
                                                            onclick={() => unPinVoice(voice)}>
                                                            <i class="bi bi-pin-angle-fill" aria-hidden="true"></i>
                                                        </button>
                                                    {:else}
                                                        <button type="button" 
                                                            id="pin_{voice.name}" 
                                                            class="btn btn-light btn-sm" 
															aria-label={m.voice_manager_pin_label({ name: voice.name })} 
                                                            onclick={() => pinVoice(voice)}>
                                                            <i class="bi bi-pin" aria-hidden="true"></i>
                                                        </button>
                                                    {/if}
                                                    <button type="button" 
                                                        id="preview_{voice.name}" 
                                                        class="btn btn-light btn-sm" 
														aria-label={m.voice_manager_preview_label({ name: voice.name })} 
                                                        onclick={() => previewVoice(voice)}>
                                                        {#if rendering && selectedVoice?.name === voice.name}
                                                            <Circle size="15" color="#1eb4b2" unit="px" duration="1s" />
                                                        {:else}
                                                            <i class="bi bi-play-circle" aria-hidden="true"></i>
                                                        {/if}
                                                    </button>
                                                    <p class="mt-2 mb-1">
														{#each voice.styles as style (style)}
															<span class="badge bg-light me-1">{style}</span>
														{/each}
														{#if voice.provider === LOCAL_TTS_PROVIDER && parentDefaultVoiceId && voice.id === parentDefaultVoiceId}
															<span class="badge bg-primary me-1">Default</span>
														{/if}
														{#if voice.provider === WEB_SPEECH_PROVIDER && isLowQualityVoice(voice)}
															<span class="badge bg-warning text-dark me-1">{m.voice_manager_low_quality_badge()}</span>
														{/if}
														{#if voice.provider === WEB_SPEECH_PROVIDER && isNetworkDependentVoice(voice)}
															<span class="badge bg-info text-dark me-1">{m.voice_manager_cloud_voice_badge()}</span>
														{/if}
													</p>
                                                </div>
                                            </div>
                                        </div>
                                    {/each}
                                {/if}
                                {#if gender === "all" || gender === "female"}
									{#each (voiceCatalog?.female || []).filter((v) => {
										return serviceProvider === "all" || serviceProvider === v.provider;
									}) as voice (voice.id || voice.name)}
										<div class="col-4">
											<div class="card border-secondary">
												<div class="card-body">
													<h5 class="card-title"><i class="bi bi-person-circle"></i> {voice.name}</h5>
													<h6 class="card-subtitle mb-2 text-muted small">{m.voice_manager_gender_female()}</h6>
													{#if voiceState.findIndex((v) => {
														return v.name === voice.name;
													}) > -1}
														<button aria-label={m.voice_manager_unpin_label({ name: voice.name })} type="button" title={m.voice_manager_unpin_title()} class="btn btn-primary btn-sm" onclick={() => unPinVoice(voice)}><i class="bi bi-pin-angle-fill"></i></button>
													{:else}
														<button aria-label={m.voice_manager_pin_label({ name: voice.name })} type="button" title={m.voice_manager_pin_title()} class="btn btn-light btn-sm" onclick={() => pinVoice(voice)}><i class="bi bi-pin"></i></button>
													{/if}
													<button type="button" class="btn btn-light btn-sm" onclick={() => previewVoice(voice)}>
														{#if rendering && selectedVoice?.name === voice.name}
															<Circle size="15" color="#1eb4b2" unit="px" duration="1s" />
														{:else}
															<i class="bi bi-play-circle"></i>
														{/if}
													</button>
													<p class="mt-2 mb-1">
														{#each voice.styles as style (style)}
															<span class="badge bg-light me-1">{style}</span>
														{/each}
														{#if voice.provider === LOCAL_TTS_PROVIDER && parentDefaultVoiceId && voice.id === parentDefaultVoiceId}
															<span class="badge bg-primary me-1">Default</span>
														{/if}
														{#if voice.provider === WEB_SPEECH_PROVIDER && isLowQualityVoice(voice)}
															<span class="badge bg-warning text-dark me-1">{m.voice_manager_low_quality_badge()}</span>
														{/if}
														{#if voice.provider === WEB_SPEECH_PROVIDER && isNetworkDependentVoice(voice)}
															<span class="badge bg-info text-dark me-1">{m.voice_manager_cloud_voice_badge()}</span>
														{/if}
													</p>
												</div>
											</div>
										</div>
									{/each}
								{/if}
                            </div>
                        </div>
                    </div>
                    <div class="col-3">
                        <p class="lead" id="pinnedVoicesSection">{m.voice_manager_pinned_voices_label()}</p>
                        <div class="container rounded py-3 scrollArea" role="region" aria-labelledby="pinnedVoicesSection">
                            <ul class="list-group">
								{#each voiceState as pinnedVoice (pinnedVoice.id || pinnedVoice.voiceURI || pinnedVoice.name)}
									<li class="list-group-item">
									{pinnedVoice.name} <button aria-label={m.voice_manager_unpin_label({ name: pinnedVoice.name })} type="button" title={m.voice_manager_unpin_title()} class="btn btn-primary btn-sm float-end" onclick={() => unPinVoice(pinnedVoice)}><i class="bi bi-pin-angle-fill"></i></button>
										<p class="text-muted small m-0 text-capitalize">{pinnedVoice.gender} | {pinnedVoice.provider}</p>
									</li>
								{/each}
							</ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<style lang="css">
	.scrollArea {
		height: 500px;
		overflow-y: scroll;
		background-color: rgba(0, 0, 0, 0.1);
	}
</style>
